import styled from 'styled-components';
import { useState } from 'react';

import SpotonPriceInfo from '/components/layout/SpotonPriceInfo';
import Container from '/components/Container';
import Heading from '/components/ui/Heading';
import Error from '/components/ui/Error';
import Button from '/components/ui/buttons/Button';
import { JobDestinations, JobTemplate } from '/types/jobTemplateTypes';
import Price from '/components/layout/Price';
import { colors } from '/css';
import PriceType from '/types/priceType';
import { useOrderState } from '/store/orderStore';
import PostCodeCheckerModal from '../../features/post-code-checker-modal/PostCodeCheckerModal';
import Paragraph from '/components/ui/Paragraph';
import useIsJobSupported from '/hooks/useIsJobSupported';
import ResourceType from '/types/resourceType';

interface Props {
    jobName: string;
    jobType: JobTemplate['typeKey'];
    totalCost: PriceType;
    destination: JobDestinations;
    jobFromCart: boolean;
    jobFromDraft: boolean;
    onSubmit: () => Promise<any>;
}

const slugs = {
    project: 'prosjekt',
    offer: 'tilbud',
};

export default function SubmissionSection({
    jobName,
    jobType,
    totalCost,
    destination,
    jobFromCart,
    jobFromDraft,
    onSubmit,
}: Props) {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [postCodeCheckerModalVisible, setPostCodeCheckerModalVisible] =
        useState<boolean>(false);

    const postCode = useOrderState((state) => state.postCode);
    const postCodeValid = useOrderState((state) => state.postCodeValid);
    const postCodeNotificationRequested = useOrderState(
        (state) => state.postCodeNotificationRequested
    );
    const isJobSupported = useIsJobSupported({
        resourceType: ResourceType.CART,
    });
    const jobSupportedInPostCode = isJobSupported(jobType);
    const jobCanBeAddedToCart = !(
        destination === JobDestinations.CART &&
        !!postCode &&
        (postCodeValid === false || !jobSupportedInPostCode)
    );

    const submitButtonLabel = () => {
        if (destination === JobDestinations.CART) {
            return !jobFromCart ? 'Legg i handlekurv' : 'Lagre';
        }

        return jobFromDraft
            ? 'Oppdater jobb'
            : `Legg til jobb i ${slugs[destination]}`;
    };

    const submit = async () => {
        if (submitting) return;

        if (
            destination === JobDestinations.CART &&
            (!postCode || postCodeValid === false || !jobSupportedInPostCode)
        ) {
            setPostCodeCheckerModalVisible(true);
            return;
        }

        setSubmitting(true);
        setError('');

        const { error } = (await onSubmit()) || {};
        setSubmitting(false);

        if (error) setError(error);
    };

    return (
        <Wrapper className="pt-32 pb-80">
            {postCodeCheckerModalVisible && (
                <PostCodeCheckerModal
                    closeRequestCallback={() =>
                        setPostCodeCheckerModalVisible(false)
                    }
                    jobTypeContext={jobType}
                    onContinue={submit}
                />
            )}
            <Container flex wrap justify="space-between">
                <div className="left">
                    <SpotonPriceInfo />
                </div>
                <div className="right">
                    <Heading medium className="mb-48" tag="div">
                        Endelig fastpris
                    </Heading>
                    <Price
                        jobToatlCost={totalCost}
                        withTax={false}
                        label={jobName}
                        className="mb-60"
                    />
                    <Error>{error}</Error>
                    <Button
                        id="submit-configuration"
                        fullWidth
                        busy={submitting}
                        onClick={submit}
                    >
                        <span>{submitButtonLabel()}</span>
                    </Button>
                    {!jobCanBeAddedToCart && postCodeNotificationRequested && (
                        <Paragraph
                            small
                            className="submission-disabled-message"
                        >
                            Takk for din interesse, vi holder deg informert på
                            e-post.
                        </Paragraph>
                    )}
                    {postCode &&
                        !jobSupportedInPostCode &&
                        process.env.NEXT_PUBLIC_ENVIRONMENT !==
                            'production' && (
                            <button
                                style={{
                                    color: 'orange',
                                    margin: '20px 0 0 0',
                                    display: 'flex',
                                    cursor: 'pointer',
                                }}
                                onClick={onSubmit}
                            >
                                Force add to cart (local & staging only)
                            </button>
                        )}
                </div>
            </Container>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    position: relative;
    &:before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${colors.mintM4};
        z-index: -1;
    }
    .left {
        width: 30%;
        > div {
            height: 100%;
        }
        @media (max-width: 1024px) {
            width: 40%;
        }
        @media (max-width: 768px) {
            order: 2;
            width: 100%;
            > div {
                height: auto;
            }
        }
    }
    .right {
        width: 50%;
        position: relative;
        @media (max-width: 768px) {
            min-width: 100%;
            width: 100%;
            order: 1;
            margin-bottom: 32px;
        }
    }
    .total-price span {
        width: 100%;
    }
    .submission-disabled-message {
        margin-top: 18px;
        @media (max-width: 767px) {
            text-align: center;
            br {
                display: block;
            }
        }
    }
`;
